import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useMasterData } from '../contexts/MasterDataContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format, addDays, addWeeks, addMonths, addYears } from 'date-fns';
import { 
  Plus, Trash2, Edit2, Calendar, Clock, Building, 
  AlertTriangle, Loader2, CheckCircle, XCircle,
  Repeat, Ban, RefreshCw, Eye, EyeOff
} from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';

const BLOCK_REASONS = ['Maintenance', 'Event', 'Coaching', 'Tournament', 'Private Booking', 'Academy', 'Bulk Booking', 'Other'];
const WEEKDAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const TIME_SLOTS = [];
for (let hour = 6; hour <= 26; hour++) {
  const displayHour = hour > 23 ? hour - 24 : hour;
  const ampm = displayHour < 12 ? 'AM' : 'PM';
  const displayHour12 = displayHour === 0 ? 12 : displayHour > 12 ? displayHour - 12 : displayHour;
  TIME_SLOTS.push(`${String(displayHour12).padStart(2, '0')}:00 ${ampm}`);
  if (hour < 26) TIME_SLOTS.push(`${String(displayHour12).padStart(2, '0')}:30 ${ampm}`);
}

const getReasonIcon = (reason) => {
  switch (reason) {
    case 'Maintenance': return '🔧';
    case 'Event': return '🎉';
    case 'Coaching': return '🏸';
    case 'Tournament': return '🏆';
    case 'Private Booking': return '🔒';
    case 'Academy': return '🎓';
    case 'Bulk Booking': return '📦';
    default: return '⛔';
  }
};

const getReasonColor = (reason) => {
  switch (reason) {
    case 'Maintenance': return 'bg-amber-100 border-amber-300 text-amber-800';
    case 'Event': return 'bg-purple-100 border-purple-300 text-purple-800';
    case 'Coaching': return 'bg-blue-100 border-blue-300 text-blue-800';
    case 'Tournament': return 'bg-green-100 border-green-300 text-green-800';
    case 'Private Booking': return 'bg-slate-100 border-slate-300 text-slate-800';
    case 'Academy': return 'bg-indigo-100 border-indigo-300 text-indigo-800';
    case 'Bulk Booking': return 'bg-cyan-100 border-cyan-300 text-cyan-800';
    default: return 'bg-red-100 border-red-300 text-red-800';
  }
};

const formatBlockWeekdays = (dows) => {
  if (!dows || dows.length === 0) return '';
  return WEEKDAYS.filter(w => dows.includes(w.value)).map(w => w.label).join(', ');
};

export default function SlotInventoryPage() {
  const { isStaff } = useAuth();
  const { getVenueNames, getGamesForVenue, getCourtsForVenueGame } = useMasterData();
  const VENUES = getVenueNames();
  
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [venueFilter, setVenueFilter] = useState('all');
  const [gameFilter, setGameFilter] = useState('all');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState(format(new Date(), 'yyyy-MM'));
  const [showInactive, setShowInactive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSyncRevenue = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/slot-blocks/sync-revenue`);
      toast.success(`Bulk revenue synced — ${res.data.total_bulk_rows} entries now in Bookings`);
      fetchBlocks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync bulk revenue');
    } finally {
      setSyncing(false);
    }
  };
  
  const [formData, setFormData] = useState({
    venue: '',
    game: '',
    court: '',
    reason: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
    start_time: '06:00 AM',
    end_time: '10:00 PM',
    days_of_week: [],
    continue_monthly: true,
    notes: '',
    pricing_type: 'fixed_monthly',
    monthly_amount: 0,
    hourly_rate: 0,
    instructed_by: '',
    entered_by: ''
  });

  useEffect(() => {
    // Auto-deactivate expired blocks once on load
    axios.post(`${API}/slot-blocks/deactivate-expired`).catch(() => {});
    fetchBlocks();
  }, [venueFilter, monthFilter, showInactive]);

  const fetchBlocks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (venueFilter !== 'all') params.venue = venueFilter;
      if (monthFilter) params.month = monthFilter;
      // Fetch all blocks (active + inactive); we filter in UI via showInactive toggle
      const response = await axios.get(`${API}/slot-blocks`, { params });
      setBlocks(response.data.blocks);
    } catch (error) {
      console.error('Failed to fetch blocks:', error);
      toast.error('Failed to load slot blocks');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Reset court when game changes
      if (field === 'game') {
        updated.court = '';
      }
      return updated;
    });
  };

  const toggleWeekday = (dayValue) => {
    setFormData(prev => {
      const set = new Set(prev.days_of_week);
      if (set.has(dayValue)) set.delete(dayValue); else set.add(dayValue);
      return { ...prev, days_of_week: Array.from(set).sort((a, b) => a - b) };
    });
  };

  const openCreateDialog = () => {
    setEditingBlock(null);
    setFormData({
      venue: '',
      game: '',
      court: '',
      reason: '',
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: '',
      start_time: '06:00 AM',
      end_time: '10:00 PM',
      days_of_week: [],
      continue_monthly: true,
      pricing_type: 'fixed_monthly',
      monthly_amount: 0,
      hourly_rate: 0,
      notes: '',
      instructed_by: '',
      entered_by: ''
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (block) => {
    setEditingBlock(block);
    const isOpenEnded = block.continue_monthly ?? (block.end_date === '2099-12-31');
    setFormData({
      venue: block.venue,
      game: block.game || '',
      court: block.court,
      reason: block.reason,
      start_date: block.start_date,
      end_date: isOpenEnded ? '' : (block.end_date || ''),
      start_time: block.start_time,
      end_time: block.end_time,
      days_of_week: block.days_of_week || [],
      continue_monthly: isOpenEnded,
      pricing_type: block.pricing_type || 'fixed_monthly',
      monthly_amount: block.monthly_amount || 0,
      hourly_rate: block.hourly_rate || 0,
      notes: block.notes || '',
      instructed_by: block.instructed_by || '',
      entered_by: block.entered_by || ''
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.venue || !formData.game || !formData.court || !formData.reason || !formData.notes || !formData.instructed_by || !formData.entered_by) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.days_of_week || formData.days_of_week.length === 0) {
      toast.error('Select at least one day of the week');
      return;
    }
    if (!formData.continue_monthly && !formData.end_date) {
      toast.error('Set an End Date or turn on "Continue automatically each month"');
      return;
    }

    setSaving(true);
    try {
      if (editingBlock) {
        await axios.put(`${API}/slot-blocks/${editingBlock.id}`, formData);
        toast.success('Slot block updated successfully');
      } else {
        await axios.post(`${API}/slot-blocks`, formData);
        toast.success('Slot block created successfully');
      }
      setIsDialogOpen(false);
      fetchBlocks();
    } catch (error) {
      console.error('Failed to save block:', error);
      toast.error(error.response?.data?.detail || 'Failed to save slot block');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (blockId) => {
    if (!window.confirm('Are you sure you want to delete this block? The court will become available again.')) {
      return;
    }

    try {
      await axios.delete(`${API}/slot-blocks/${blockId}`);
      toast.success('Slot block deleted successfully');
      fetchBlocks();
    } catch (error) {
      console.error('Failed to delete block:', error);
      toast.error('Failed to delete slot block');
    }
  };

  const allActiveBlocks = blocks.filter(b => showInactive ? !b.is_active : b.is_active);
  const gameOptions = Array.from(new Set(allActiveBlocks.map(b => b.game).filter(Boolean))).sort();
  const reasonOptions = Array.from(new Set(allActiveBlocks.map(b => b.reason).filter(Boolean))).sort();
  const clientOptions = Array.from(new Set(allActiveBlocks.map(b => b.instructed_by).filter(Boolean))).sort();
  const activeBlocks = allActiveBlocks.filter(b =>
    (gameFilter === 'all' || b.game === gameFilter) &&
    (reasonFilter === 'all' || b.reason === reasonFilter) &&
    (clientFilter === 'all' || b.instructed_by === clientFilter)
  );
  // Revenue counters (honour the active filters)
  const totalMonthlyAmount = activeBlocks.reduce((s, b) => s + (b.bulk_revenue?.monthly_amount || 0), 0);
  const totalMonthlyHours = activeBlocks.reduce((s, b) => s + (b.bulk_revenue?.monthly_hours || 0), 0);
  const effectiveRate = totalMonthlyHours > 0 ? totalMonthlyAmount / totalMonthlyHours : 0;
  const totalCancelledHours = activeBlocks.reduce((s, b) => s + (b.bulk_revenue?.cancelled_hours || 0), 0);
  const totalCancelledValue = activeBlocks.reduce((s, b) => s + (b.bulk_revenue?.cancelled_value || 0), 0);
  const groupedBlocks = activeBlocks.reduce((acc, block) => {
    const key = block.venue;
    if (!acc[key]) acc[key] = [];
    acc[key].push(block);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="slot-inventory-page">
        {/* Header */}
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
            Bulk Blocks
          </h1>
          <p className="text-slate-600 mt-1">Manage court availability and blocking</p>
        </div>
        <div className="flex justify-center gap-2">
          {!isStaff && (
            <>
              <Button onClick={openCreateDialog} className="gap-2" data-testid="add-block-btn">
                <Plus className="w-4 h-4" />
                Block Slots
              </Button>
              <Button
                onClick={handleSyncRevenue}
                variant="outline"
                className="gap-2"
                disabled={syncing}
                data-testid="sync-revenue-btn"
                title="Regenerate Bulk Book revenue entries in Bookings for this month"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing…' : 'Sync Revenue'}
              </Button>
            </>
          )}
          <Button
            variant={showInactive ? 'default' : 'outline'}
            className="gap-2"
            onClick={() => setShowInactive(v => !v)}
            data-testid="toggle-inactive-btn"
          >
            {showInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {showInactive ? 'Showing Inactive' : 'Show Inactive'}
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4 border-slate-200">
          <div className="flex items-center gap-4 flex-wrap">
            <Label className="text-slate-700 font-medium whitespace-nowrap">Month:</Label>
            <Input
              type="month"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="w-44"
              data-testid="block-month-filter"
            />
            <Label className="text-slate-700 font-medium whitespace-nowrap">Filter by Venue:</Label>
            <Select value={venueFilter} onValueChange={setVenueFilter}>
              <SelectTrigger className="w-48" data-testid="venue-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Venues</SelectItem>
                {VENUES.map(venue => (
                  <SelectItem key={venue} value={venue}>{venue}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label className="text-slate-700 font-medium whitespace-nowrap">Game:</Label>
            <Select value={gameFilter} onValueChange={setGameFilter}>
              <SelectTrigger className="w-40" data-testid="block-game-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Games</SelectItem>
                {gameOptions.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label className="text-slate-700 font-medium whitespace-nowrap">Reason:</Label>
            <Select value={reasonFilter} onValueChange={setReasonFilter}>
              <SelectTrigger className="w-44" data-testid="block-reason-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reasons</SelectItem>
                {reasonOptions.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label className="text-slate-700 font-medium whitespace-nowrap">Client:</Label>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-44" data-testid="block-client-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clientOptions.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2 text-sm text-slate-600">
              <Ban className="w-4 h-4 text-red-500" />
              <span data-testid="active-blocks-count">{activeBlocks.length} active block(s)</span>
            </div>
          </div>

          {/* Revenue counters — honour the active filters. Hidden for Staff. */}
          {!isStaff && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="bulk-revenue-summary">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-xs text-emerald-700 font-medium">Total Monthly Amount</p>
              <p className="text-xl font-bold text-emerald-800 mt-0.5" data-testid="summary-monthly-amount">
                ₹{Number(totalMonthlyAmount).toLocaleString('en-IN')}
              </p>
            </div>
            <div className="rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3">
              <p className="text-xs text-cyan-700 font-medium">Total Monthly Hours</p>
              <p className="text-xl font-bold text-cyan-800 mt-0.5" data-testid="summary-monthly-hours">
                {Number(totalMonthlyHours).toLocaleString('en-IN')} hrs
              </p>
            </div>
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
              <p className="text-xs text-indigo-700 font-medium">Effective Rate</p>
              <p className="text-xl font-bold text-indigo-800 mt-0.5" data-testid="summary-effective-rate">
                ₹{Number(Math.round(effectiveRate)).toLocaleString('en-IN')}/hr
              </p>
            </div>
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
              <p className="text-xs text-rose-700 font-medium">Cancelled Hours</p>
              <p className="text-xl font-bold text-rose-800 mt-0.5" data-testid="summary-cancelled-hours">
                {Number(totalCancelledHours).toLocaleString('en-IN')} hrs
              </p>
            </div>
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
              <p className="text-xs text-rose-700 font-medium">Cancelled Value</p>
              <p className="text-xl font-bold text-rose-800 mt-0.5" data-testid="summary-cancelled-value">
                ₹{Number(totalCancelledValue).toLocaleString('en-IN')}
              </p>
            </div>
          </div>
          )}
        </Card>

        {/* Loading */}
        {loading && (
          <Card className="p-12 text-center border-slate-200">
            <Loader2 className="w-12 h-12 mx-auto mb-3 text-primary animate-spin" />
            <p className="text-slate-600">Loading slot blocks...</p>
          </Card>
        )}

        {/* Blocks List */}
        {!loading && activeBlocks.length === 0 && (
          <Card className="p-12 text-center border-slate-200">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
            <p className="text-slate-600 mb-2">No slots are currently blocked</p>
            <p className="text-sm text-slate-400">All courts are available for booking</p>
          </Card>
        )}

        {!loading && Object.entries(groupedBlocks).map(([venue, venueBlocks]) => (
          <Card key={venue} className="border-slate-200 overflow-hidden" data-testid={`venue-blocks-${venue}`}>
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <Building className="w-5 h-5 text-slate-600" />
                <h2 className="font-heading font-semibold text-lg text-slate-900">{venue}</h2>
                <span className="ml-auto px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                  {venueBlocks.length} block(s)
                </span>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {venueBlocks.map(block => (
                <div key={block.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className={`px-3 py-2 rounded-lg border ${getReasonColor(block.reason)}`}>
                      <span className="text-lg">{getReasonIcon(block.reason)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-900">{block.court}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getReasonColor(block.reason)}`}>
                          {block.reason}
                        </span>
                        {(block.days_of_week && block.days_of_week.length > 0) && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                            <Repeat className="w-3 h-3" />
                            {formatBlockWeekdays(block.days_of_week)}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {(block.continue_monthly ?? (block.end_date === '2099-12-31'))
                            ? `From ${block.start_date} · every month`
                            : `${block.start_date} to ${block.end_date}`}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {block.start_time} - {block.end_time}
                        </span>
                      </div>
                      {block.notes && (
                        <p className="mt-2 text-sm text-slate-500 italic">{block.notes}</p>
                      )}
                      {(block.instructed_by || block.entered_by) && (
                        <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                          {block.instructed_by && (
                            <span>Client: <span className="font-medium text-slate-700">{block.instructed_by}</span></span>
                          )}
                          {block.entered_by && (
                            <span>Entered by: <span className="font-medium text-slate-700">{block.entered_by}</span></span>
                          )}
                        </div>
                      )}
                      {block.bulk_revenue && (
                        <div
                          className="mt-2 flex flex-wrap items-center gap-3 text-xs"
                          data-testid={`bulk-revenue-${block.id}`}
                        >
                          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                            {block.bulk_revenue.pricing_type === 'fixed_hourly' ? 'Per-hour' : 'Monthly'}
                          </span>
                          <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                            Monthly: ₹{Number(block.bulk_revenue.monthly_amount).toLocaleString('en-IN')}
                          </span>
                          <span className="px-2 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-200">
                            Total monthly hours: <span className="font-semibold">{block.bulk_revenue.monthly_hours} hrs</span>
                          </span>
                          <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200">
                            Effective rate: <span className="font-semibold">₹{Number(block.bulk_revenue.hourly_rate).toLocaleString('en-IN')}/hr</span>
                          </span>
                          {block.bulk_revenue.cancelled_hours > 0 && (
                            <>
                              <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200" data-testid={`cancelled-hours-${block.id}`}>
                                Cancelled hours: <span className="font-semibold">{block.bulk_revenue.cancelled_hours} hrs</span>
                              </span>
                              <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200" data-testid={`cancelled-value-${block.id}`}>
                                Cancelled value: <span className="font-semibold">₹{Number(block.bulk_revenue.cancelled_value).toLocaleString('en-IN')}</span>
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!isStaff && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(block)}
                            data-testid={`edit-block-${block.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(block.id)}
                            data-testid={`delete-block-${block.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-3xl lg:max-w-4xl w-[95vw] max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                {editingBlock ? 'Edit Slot Block' : 'Block Court Slots'}
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 py-2">
              {/* Venue */}
              <div className="space-y-1.5">
                <Label>Venue <span className="text-red-500">*</span></Label>
                <Select value={formData.venue} onValueChange={(v) => handleInputChange('venue', v)}>
                  <SelectTrigger data-testid="block-venue-select">
                    <SelectValue placeholder="Select venue" />
                  </SelectTrigger>
                  <SelectContent>
                    {VENUES.map(venue => (
                      <SelectItem key={venue} value={venue}>{venue}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Game */}
              <div className="space-y-1.5">
                <Label>Game <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.game}
                  onValueChange={(v) => handleInputChange('game', v)}
                  disabled={!formData.venue}
                >
                  <SelectTrigger data-testid="block-game-select">
                    <SelectValue placeholder={formData.venue ? "Select game" : "Select venue first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {getGamesForVenue(formData.venue).map(game => (
                      <SelectItem key={game} value={game}>{game}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Court */}
              <div className="space-y-1.5">
                <Label>Court <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.court}
                  onValueChange={(v) => handleInputChange('court', v)}
                  disabled={!formData.venue || !formData.game}
                >
                  <SelectTrigger data-testid="block-court-select">
                    <SelectValue placeholder={formData.game ? "Select court" : "Select game first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.venue && formData.game && getCourtsForVenueGame(formData.venue, formData.game).map(court => (
                      <SelectItem key={court} value={court}>{court}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Reason */}
              <div className="space-y-1.5">
                <Label>Reason <span className="text-red-500">*</span></Label>
                <Select value={formData.reason} onValueChange={(v) => handleInputChange('reason', v)}>
                  <SelectTrigger data-testid="block-reason-select">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {BLOCK_REASONS.map(reason => (
                      <SelectItem key={reason} value={reason}>
                        <span className="flex items-center gap-2">
                          {getReasonIcon(reason)} {reason}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Start Time (30-min steps) */}
              <div className="space-y-1.5">
                <Label>Start Time</Label>
                <Select value={formData.start_time} onValueChange={(v) => handleInputChange('start_time', v)}>
                  <SelectTrigger data-testid="block-start-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* End Time (30-min steps) */}
              <div className="space-y-1.5">
                <Label>End Time</Label>
                <Select value={formData.end_time} onValueChange={(v) => handleInputChange('end_time', v)}>
                  <SelectTrigger data-testid="block-end-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Repeat — weekday multi-select (full width) */}
              <div className="space-y-1.5 sm:col-span-2 lg:col-span-3">
                <Label>Repeat on <span className="text-red-500">*</span></Label>
                <div className="flex flex-wrap gap-2" data-testid="block-weekday-group">
                  {WEEKDAYS.map(day => {
                    const selected = formData.days_of_week.includes(day.value);
                    return (
                      <button
                        type="button"
                        key={day.value}
                        onClick={() => toggleWeekday(day.value)}
                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                          selected
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-400'
                        }`}
                        data-testid={`block-weekday-${day.value}`}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">Booking recurs on the selected days starting from the Start Date.</p>
              </div>

              {/* Continue monthly toggle (full width) */}
              <div className="sm:col-span-2 lg:col-span-3 flex items-center justify-between bg-slate-50 rounded-lg p-3">
                <div className="pr-3">
                  <p className="text-sm font-medium text-slate-800">Continue automatically each month</p>
                  <p className="text-xs text-slate-500">When on, the block repeats every month with the same days. Turn off to set an End Date.</p>
                </div>
                <Switch
                  checked={formData.continue_monthly}
                  onCheckedChange={(v) => handleInputChange('continue_monthly', v)}
                  data-testid="block-continue-monthly"
                />
              </div>

              {/* Start Date */}
              <div className="space-y-1.5">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  data-testid="block-start-date-input"
                />
              </div>
              {/* End Date */}
              {!formData.continue_monthly && (
                <div className="space-y-1.5">
                  <Label>End Date <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                    data-testid="block-end-date-input"
                  />
                </div>
              )}

              {/* Pricing Type — only for revenue reasons (not Maintenance/Event) */}
              {formData.reason && !['Maintenance', 'Event'].includes(formData.reason) && (
                <>
                  <div className="space-y-1.5">
                    <Label>Bulk Pricing Type</Label>
                    <Select value={formData.pricing_type} onValueChange={(v) => handleInputChange('pricing_type', v)}>
                      <SelectTrigger data-testid="block-pricing-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed_monthly">Fixed Monthly Amount</SelectItem>
                        <SelectItem value="fixed_hourly">Fixed Per Hour Rate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.pricing_type === 'fixed_hourly' ? (
                    <div className="space-y-1.5">
                      <Label>Fixed Per Hour Rate (₹/hr)</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="e.g. 500"
                        value={formData.hourly_rate || ''}
                        onChange={(e) => handleInputChange('hourly_rate', parseFloat(e.target.value) || 0)}
                        data-testid="block-hourly-rate-input"
                      />
                      <p className="text-xs text-slate-500">
                        Monthly amount = rate × hours used (weekdays × duration).
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <Label>Fixed Monthly Amount (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="e.g. 30000"
                        value={formData.monthly_amount || ''}
                        onChange={(e) => handleInputChange('monthly_amount', parseFloat(e.target.value) || 0)}
                        data-testid="block-monthly-amount-input"
                      />
                      <p className="text-xs text-slate-500">
                        Split across this month's usage days as daily "Bulk Book" revenue.
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Client Name */}
              <div className="space-y-1.5">
                <Label>Client Name <span className="text-red-500">*</span></Label>
                <Input
                  list="client-name-options"
                  placeholder="Select or type client name"
                  value={formData.instructed_by}
                  onChange={(e) => handleInputChange('instructed_by', e.target.value)}
                  data-testid="block-client-name-input"
                  required
                />
                <datalist id="client-name-options">
                  {clientOptions.map(c => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              {/* Entered By */}
              <div className="space-y-1.5">
                <Label>Entered By <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="Who is entering this block?"
                  value={formData.entered_by}
                  onChange={(e) => handleInputChange('entered_by', e.target.value)}
                  data-testid="block-entered-by-input"
                  required
                />
              </div>

              {/* Notes (full width) */}
              <div className="space-y-1.5 sm:col-span-2 lg:col-span-3">
                <Label>Notes <span className="text-red-500">*</span></Label>
                <Textarea
                  placeholder="Add notes about this block (required)..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={2}
                  data-testid="block-notes-textarea"
                  required
                />
              </div>

              {/* Info Box (full width, condensed) */}
              <div className="sm:col-span-2 lg:col-span-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    Blocking PREVENTS new bookings during the blocked time. Existing bookings are kept (notification emails sent to maksmiraroad@gmail.com). You can edit or remove blocks anytime.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-block-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingBlock ? 'Update Block' : 'Create Block'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
